import pattern from 'patternomaly';
import { featureCollection } from '@turf/turf';
import { colorUtils } from '../../Utils/color.utils';
import { getSymbologyColor } from '../../Utils/symbology.utils';
import { IResult, IResultLayer } from '../interfaces/result.interface';
import mapboxgl, { FillLayer, LineLayer, CircleLayer, FillPaint, AnySourceData, AnyLayer } from 'mapbox-gl';
import { ChartPattern } from '../enums/chart-pattern.enum';
import { LAYER_NAMES, LAYER_TYPES } from '../../Utils/layer';

export type ResultSource = {
  id: string;
  data: AnySourceData;
  layerType?: (typeof LAYER_TYPES)[keyof typeof LAYER_TYPES];
};

export const buildLayerConfig = (resultId: string, layerIndex: number): IResultLayer => ({
  resultId: `${resultId}-Layer-${new Date().getTime() + Math.random()}`,
  displayName: `Layer ${layerIndex}`,
  data: undefined,
  imageryUri: '',
  tab: {
    visible: false,
    displayName: `Tab ${layerIndex}`,
    pieChart: [],
  },
  details: {
    visible: true,
    columnName: '',
    infoColumns: [
      { displayName: 'Area (m²)', visible: false },
      { displayName: 'Percent (%)', visible: false },
    ],
    classes: [],
  },
  inspector: {
    columnsToDisplay: [],
    displayNamesDictionary: {},
  },
});

export const buildChartConfig = (categories) =>
  categories
    ?.sort((a, b) => (a < b ? -1 : 1))
    .map((category) => ({
      className: category,
      displayName: category,
      percent: 100 / categories.length,
      fillColor: getSymbologyColor(`${category}`) || colorUtils.generateHexColorFromString(`${category}`),
      fillTransparency: 0,
      fillPattern: undefined,
    }));

export const buildDetailsConfig = (column, chartData) => {
  return {
    visible: true,
    columnName: column,
    defaultEnabled: true,
    infoColumns: [
      { displayName: 'Area(m²)', visible: true },
      { displayName: 'Percent(%)', visible: true },
    ],
    classes: chartData.map((c) => ({
      category: c.className,
      displayName: c.className,
      fillColor: c.fillColor,
      fillTransparency: c.fillTransparency,
      fillPattern: c.fillPattern,
      defaultEnabled: true,
      edgeColor: c.fillColor,
      edgeTransparency: c.fillTransparency,
      edgeThickness: 1,
      infoValues: [c.percent, c.percent],
    })),
  };
};

/**
 * Which Mapbox layer type can actually draw this collection.
 *
 * Mapbox picks a renderer per layer type and they do not cross over: a `fill` layer given point
 * geometry has no ring to fill and silently draws nothing. Points therefore need `circle`, which is
 * why this is an explicit mapping rather than the previous "LineString or else fill" test.
 *
 * The type is taken from the first feature carrying a geometry — a class group is one value of one
 * column, so in practice its features share a geometry type. Anything unrecognised keeps the old
 * fill behaviour rather than vanishing.
 */
const layerTypeOf = (data: GeoJSON.FeatureCollection) => {
  const geometryType = data.features?.find((feature) => !!feature?.geometry)?.geometry?.type;

  switch (geometryType) {
    case 'Point':
    case 'MultiPoint':
      return LAYER_TYPES.Circle;
    case 'LineString':
    case 'MultiLineString':
      return LAYER_TYPES.Line;
    default:
      return LAYER_TYPES.Fill;
  }
};

const buildSourcesV1 = (layer: IResultLayer, data: GeoJSON.FeatureCollection) => {
  const property = layer.details.columnName;
  const sources: ResultSource[] = [];

  if (!!property) {
    const featuresByCategory = data.features.reduce((group, feature) => {
      const category = feature.properties[property]?.toString().replace(/\s/g, '-');
      group[category] = group[category] || [];
      group[category].push(feature);
      return group;
    }, {});

    const features = Object.keys(featuresByCategory)?.map((category) => {
      const collection = featureCollection(featuresByCategory[category]);
      return {
        id: `${layer.resultId}_${property}_${category}`,
        data: collection,
      };
    });

    features
      ?.map((feature) => ({
        id: feature.id,
        data: {
          type: 'geojson' as const,
          data: feature.data as GeoJSON.FeatureCollection,
        },
        layerType: layerTypeOf(feature.data as GeoJSON.FeatureCollection),
      }))
      .forEach((source) => sources.push(source));
  }

  if (layer.imageryUri) {
    sources.push({
      id: `${layer.resultId}_${property}_imagery`,
      data: {
        type: 'raster',
        url: layer.imageryUri,
      },
    });
  }

  return sources;
};

export const buildSources = (
  layer: IResultLayer,
  shapes?: { data: GeoJSON.FeatureCollection; fileName: string; key: string }[],
): ResultSource[] => {
  if (!shapes?.length && !!layer.data) {
    return buildSourcesV1(layer, layer.data); // Compatibility with old data
  }

  if (!shapes?.length || !layer.shapeKeys?.length) return [];

  const data = featureCollection(
    shapes
      ?.filter((shape) => layer.shapeKeys.includes(shape.key))
      ?.map((shape) => shape.data.features)
      ?.flat(),
  );

  return buildSourcesV1(layer, data);
};

export const buildLayersPatterns = (sources: ResultSource[], result: IResult): Record<string, string> => {
  const map: Record<string, string> = {};

  sources?.forEach((source) => {
    const [resultId, , value] = source.id.split('_');
    const layerResult = result.layers.find((layer) => layer.resultId === resultId);
    const detailClass = layerResult?.details?.classes?.find(
      (c) => c.category.toString().replace(/\s/g, '-') === value,
    );

    if (
      !detailClass?.fillPattern ||
      detailClass?.fillPattern === ChartPattern.None ||
      detailClass?.fillPattern === ChartPattern.Undefined
    )
      return;

    const imgPattern = pattern.draw(
      detailClass.fillPattern as Exclude<ChartPattern, ChartPattern.None | ChartPattern.Undefined>,
      colorUtils.hexToRGB(detailClass.fillColor, 1 - (detailClass.fillTransparency || 0)),
    );

    const size = 20;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = size;
    canvas.height = size;

    ctx.fillStyle = imgPattern;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    map[`pattern_${source.id}`] = canvas.toDataURL();
  });

  return map;
};

export const buildLayersImagery = (sources: ResultSource[]): AnyLayer[] => {
  return (
    sources
      ?.filter((source) => source.data.type === 'raster')
      ?.map((source) => {
        return {
          id: source.id,
          type: 'raster',
          source: source.id,
        };
      }) || []
  );
};

export const buildLayersFill = (sources: ResultSource[], result: IResult) => {
  return (
    sources
      ?.filter((source) => source.data.type !== 'raster')
      ?.reduce((acc, source) => {
        const [resultId, , value] = source.id.split('_');
        const layerResult = result.layers.find((layer) => layer.resultId === resultId);

        const detailClass = layerResult?.details?.classes?.find(
          (c) => c.category.toString().replace(/\s/g, '-') === value,
        );

        if (!detailClass) return acc;

        const hasFillPattern =
          detailClass?.fillPattern &&
          detailClass?.fillPattern !== ChartPattern.None &&
          detailClass?.fillPattern !== ChartPattern.Undefined;

        const paint: FillPaint = hasFillPattern
          ? {
              'fill-pattern': `pattern_${source.id}`,
            }
          : {
              'fill-color': detailClass?.fillColor,
              'fill-opacity': 1 - (detailClass?.fillTransparency || 0),
              'fill-outline-color': detailClass?.edgeColor,
              'fill-antialias': true,
            };

        acc.push({
          id: source.id,
          type: 'fill',
          source: source.id,
          layout: {},
          paint,
        });

        return acc;
      }, [] as FillLayer[]) || []
  );
};

export const buildLayersLine = (sources: ResultSource[], result: IResult) => {
  return (
    sources
      ?.filter((source) => source.data.type !== 'raster')
      ?.reduce((acc, source) => {
        const [resultId, , value] = source.id.split('_');
        const layerResult = result.layers.find((layer) => layer.resultId === resultId);

        const detailClass = layerResult?.details?.classes?.find(
          (c) => c.category.toString().replace(/\s/g, '-') === value,
        );

        if (!detailClass) return acc;

        acc.push({
          id: source.id,
          type: 'line',
          source: source.id,
          layout: {},
          paint: {
            'line-color': detailClass?.fillColor || detailClass?.edgeColor,
            'line-width': 2,
            'line-opacity': 1 - (detailClass?.fillTransparency || 0),
          },
        });

        return acc;
      }, [] as LineLayer[]) || []
  );
};

/**
 * Base circle radius in screen pixels at each zoom level.
 *
 * Radius is in pixels, not metres, so a fixed value turns a wooded site into one solid mass when
 * zoomed out and leaves pin-pricks when zoomed in. Interpolating on zoom keeps a tree readable at
 * survey zoom without burying the habitat polygons underneath at overview zoom.
 */
const CIRCLE_RADIUS_BY_ZOOM: [number, number][] = [
  [10, 1.5],
  [14, 3],
  [17, 6],
  [20, 12],
];

/**
 * How much bigger each tree size draws, relative to the base radius above.
 *
 * The Individual Trees layer groups by the `Size` attribute, so a class category *is* its size and
 * the circle can be scaled from it directly. Abbreviated forms are included because the engine
 * writes them for Distinctiveness ('V.High'), and an unrecognised value falls back to the base size
 * rather than disappearing or drawing at some arbitrary scale.
 */
const SIZE_RADIUS_SCALE: Record<string, number> = {
  small: 0.6,
  medium: 1,
  large: 1.5,
  'very large': 2.2,
  'v.large': 2.2,
};

const circleRadiusFor = (category: string): mapboxgl.Expression => {
  const scale = SIZE_RADIUS_SCALE[category?.toString().trim().toLowerCase()] ?? 1;

  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    ...CIRCLE_RADIUS_BY_ZOOM.flatMap(([zoom, radius]) => [zoom, Math.round(radius * scale * 100) / 100]),
  ] as mapboxgl.Expression;
};

export const buildLayersCircle = (sources: ResultSource[], result: IResult) => {
  return (
    sources
      ?.filter((source) => source.data.type !== 'raster')
      ?.reduce((acc, source) => {
        const [resultId, , value] = source.id.split('_');
        const layerResult = result.layers.find((layer) => layer.resultId === resultId);

        const detailClass = layerResult?.details?.classes?.find(
          (c) => c.category.toString().replace(/\s/g, '-') === value,
        );

        if (!detailClass) return acc;

        acc.push({
          id: source.id,
          type: 'circle',
          source: source.id,
          layout: {},
          paint: {
            'circle-color': detailClass?.fillColor,
            'circle-opacity': 1 - (detailClass?.fillTransparency || 0),
            'circle-radius': circleRadiusFor(detailClass.category),
            'circle-stroke-color': detailClass?.edgeColor || detailClass?.fillColor,
            'circle-stroke-width': detailClass?.edgeThickness ?? 1,
            'circle-stroke-opacity': 1 - (detailClass?.edgeTransparency || 0),
          },
        });

        return acc;
      }, [] as CircleLayer[]) || []
  );
};

export const buildLayersOutline = (sources: ResultSource[], result: IResult) => {
  return (
    sources
      ?.filter((source) => source.data.type !== 'raster')
      ?.reduce((acc, source) => {
        const [resultId, , value] = source.id.split('_');
        const layerResult = result.layers.find((layer) => layer.resultId === resultId);

        const detailClass = layerResult?.details?.classes?.find(
          (c) => c.category.toString().replace(/\s/g, '-') === value,
        );

        if (!detailClass) return acc;

        acc.push({
          id: `${source.id}_outline`,
          type: 'line',
          source: source.id,
          layout: {},
          paint: {
            'line-color':
              layerResult.displayName === LAYER_NAMES.Distinctiveness ? '#363636' : detailClass?.edgeColor,
            'line-width': detailClass?.edgeThickness,
            'line-opacity': 1 - (detailClass?.edgeTransparency || 0),
          },
        });

        return acc;
      }, [] as LineLayer[]) || []
  );
};

export const buildInspectorTool = (sources: ResultSource[], result: IResult) => {
  const inspectorDataMap = new Map<string, string>();

  sources.forEach((source) => {
    const [resultId, , value] = source.id.split('_');
    const layerResult = result.layers.find((layer) => layer.resultId === resultId);
    const detailClass = layerResult?.details?.classes?.find(
      (c) => c.category.toString().replace(/\s/g, '-') === value,
    );
    if (!detailClass) return;

    const columnsIndexToDisplay = layerResult.inspector.columnsToDisplay.map((column) => {
      return layerResult.details.infoColumns.findIndex((c) => c.displayName === column);
    });

    const innerHtml = `<div>
      <div style="display: flex; flex-direction: row;align-items: center;">
        <div style="width: 12px;
            height: 12px;
            text-align: center;
            margin-right: 8px;
            border-radius: 50%;
            background-color: ${detailClass.fillColor};
            opacity: ${1 - (detailClass.fillTransparency || 0)};
            border: solid ${detailClass.edgeThickness}px ${colorUtils.hexToRGB(
              detailClass.edgeColor,
              1 - (detailClass.edgeTransparency || 0),
            )};"
        ></div>
        ${detailClass.displayName}
      </div>
        ${detailClass.infoValues
          .filter((v, i) => columnsIndexToDisplay.includes(i))
          .map((v, i) => `${layerResult.details.infoColumns[i].displayName}: ${v}`)
          .join(' | ')}
    </div>`;

    inspectorDataMap.set(source.id, innerHtml);
  });

  return inspectorDataMap;
};
